/**
 * Logger utility for controlling debug output based on environment
 * In production, only errors and warnings will be shown
 * In development, all logs will be shown
 */

// Determine if we're in production mode
const isProduction = import.meta.env.PROD === true;

// Log levels enum - used for type safety in logger configuration
enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

// Configuration for which log levels to show in each environment
const LOG_LEVEL_CONFIG = {
  production: {
    [LogLevel.DEBUG]: false,
    [LogLevel.INFO]: false,
    [LogLevel.WARN]: true,
    [LogLevel.ERROR]: true
  },
  development: {
    [LogLevel.DEBUG]: true,
    [LogLevel.INFO]: true,
    [LogLevel.WARN]: true,
    [LogLevel.ERROR]: true
  }
};

// Get the current configuration based on environment
const currentConfig = isProduction 
  ? LOG_LEVEL_CONFIG.production 
  : LOG_LEVEL_CONFIG.development;

/**
 * Logger object with methods for different log levels
 */
export const logger = {
  /**
   * Debug level logging - only shown in development
   */
  debug: (...args: any[]) => {
    if (currentConfig[LogLevel.DEBUG]) {
      console.debug('[DEBUG]', ...args);
    }
  },
  
  /**
   * Info level logging - only shown in development
   */
  info: (...args: any[]) => {
    if (currentConfig[LogLevel.INFO]) {
      console.info('[INFO]', ...args);
    }
  },
  
  /**
   * Warning level logging - shown in all environments
   */
  warn: (...args: any[]) => {
    if (currentConfig[LogLevel.WARN]) {
      console.warn('[WARN]', ...args);
    }
  },
  
  /**
   * Error level logging - shown in all environments
   */
  error: (...args: any[]) => {
    if (currentConfig[LogLevel.ERROR]) {
      console.error('[ERROR]', ...args);
    }
  }
};

/**
 * Log environment variables in a controlled manner
 * This will only output in development mode
 */
export const logEnvironmentVariables = () => {
  if (!currentConfig[LogLevel.DEBUG]) return;
  
  logger.debug('Environment Variables:');
  // We're now using WalletConnect infrastructure, so we don't need to log these RPC URLs
  // They're no longer being used for gas price estimation or network connections
};
